import React, { useEffect, useState, useRef } from 'react';
import { Eye, ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import api from '../api';



function FadeIn({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [v, setV] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return <div ref={ref} className={`transition-all duration-700 ${className}`}
    style={{ opacity: v?1:0, transform: v?'translateY(0)':'translateY(30px)', transitionDelay: `${delay}ms` }}>{children}</div>;
}

const storyColors = [
  { bg: 'bg-neonBlue/10', text: 'text-neonBlue', border: 'border-neonBlue/20' },
  { bg: 'bg-neonGold/10', text: 'text-neonGold', border: 'border-neonGold/20' },
  { bg: 'bg-purple-500/10', text: 'text-purple-400', border: 'border-purple-500/20' },
  { bg: 'bg-pink-500/10', text: 'text-pink-400', border: 'border-pink-500/20' },
  { bg: 'bg-green-500/10', text: 'text-green-400', border: 'border-green-500/20' },
];

const ITEMS_PER_PAGE = 4;

export default function SneakPeeks() {
  const [peeks, setPeeks] = useState([]);
  const [filter, setFilter] = useState('All');
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get(`/api/sneak-peeks`).then(r => setPeeks(r.data)).catch(() => {});
  }, []);

  if (peeks.length === 0) return null;

  const storyNames = [...new Set(peeks.map(p => p.story_name).filter(Boolean))];
  const filtered = filter === 'All' ? peeks : peeks.filter(p => p.story_name === filter);
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const handleFilter = (f) => { setFilter(f); setPage(1); };

  return (
    <section id="sneak-peeks" data-testid="sneak-peeks-section" className="py-20 sm:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 text-xs font-bold uppercase tracking-[0.25em] text-neonBlue border border-neonBlue/30 rounded-full mb-4 bg-neonBlue/5">
            <Eye className="inline w-3 h-3 mr-1 -mt-0.5" />
            Today's Sneak Peek
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-2">
            <span className="font-hindi text-neonGold">आज की झलक</span>
          </h2>
          <p className="text-gray-500 max-w-md mx-auto mb-6">A glimpse into today's audio episodes. Join Telegram for the full experience.</p>

          {storyNames.length > 1 && (
            <div className="flex flex-wrap justify-center gap-2" data-testid="sneak-peek-filters">
              <button onClick={() => handleFilter('All')} data-testid="filter-all"
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all ${filter === 'All' ? 'bg-white/10 text-white border border-white/20' : 'text-gray-500 border border-white/5 hover:text-white'}`}>
                All Stories
              </button>
              {storyNames.map((name, idx) => {
                const c = storyColors[idx % storyColors.length];
                return (
                  <button key={name} onClick={() => handleFilter(name)}
                    data-testid={`filter-${name.replace(/\s/g, '-').toLowerCase()}`}
                    className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-all border ${filter === name ? `${c.bg} ${c.text} ${c.border}` : 'text-gray-500 border-white/5 hover:text-white'}`}>
                    {name}
                  </button>
                );
              })}
            </div>
          )}
        </FadeIn>

        {/* Fixed height card grid - always 2 rows */}
        <div className="min-h-[420px]">
          <div className="grid sm:grid-cols-2 gap-6">
            {paginated.map((peek, i) => {
              const idx = storyNames.indexOf(peek.story_name);
              const c = storyColors[idx >= 0 ? idx % storyColors.length : 0];
              return (
                <FadeIn key={`${filter}-${page}-${peek.story_name}-${peek.title}-${i}`} delay={i * 100}>
                  <div data-testid={`sneak-peek-card-${i}`}
                    className="glass-card rounded-xl p-6 sm:p-8 relative overflow-hidden group h-full">
                    <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-neonBlue/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                      <span className={`${c.bg} ${c.text} text-[10px] font-bold px-2.5 py-0.5 rounded-full border ${c.border}`}>{peek.story_name}</span>
                      <span className="bg-white/5 text-white/70 text-xs font-bold px-3 py-1 rounded-full border border-white/10">{peek.title}</span>
                      <span className="bg-neonGold/10 text-neonGold text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse">NEW</span>
                    </div>
                    <p className="font-hindi text-gray-300 text-lg leading-relaxed">"{peek.text}"</p>
                    <div className="mt-4 flex items-center justify-between">
                      {peek.date && <span className="flex items-center gap-1 text-xs text-gray-600"><Calendar size={10} /> {peek.date}</span>}
                      <span className="text-xs text-gray-600 ml-auto">...to be continued</span>
                    </div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>

        {/* Pagination - FIXED position, always rendered */}
        <div className="flex items-center justify-center gap-2 mt-8 h-12" data-testid="sneak-peek-pagination">
          {totalPages > 1 && (
            <>
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} data-testid="peek-page-prev"
                className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:border-neonBlue/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} onClick={() => setPage(p)} data-testid={`peek-page-${p}`}
                  className={`w-9 h-9 rounded-full text-sm font-semibold transition-all ${p === page ? 'bg-neonBlue text-black' : 'border border-white/10 text-gray-400 hover:text-white hover:border-neonBlue/30'}`}>
                  {p}
                </button>
              ))}
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page === totalPages} data-testid="peek-page-next"
                className="w-9 h-9 rounded-full border border-white/10 flex items-center justify-center text-gray-400 hover:text-white hover:border-neonBlue/30 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <ChevronRight size={16} />
              </button>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
